import {
  AmountListItem,
  Button,
  Chip,
  Icon,
  InfoAlert,
  RatingFormAlert,
  ReferralCodeSection,
  Section,
  SuccessAlert,
  Table,
  TokenListItem,
  Tooltip,
  WalletTokenListItem,
} from '@/components';
import { RatingFormType } from '@/components/molecules/RatingForm/RatingForm';
import { useNavigation } from '@/components/providers';
import { TokenInterface, WalletTokenInterface } from '@/interfaces';
import { replaceTokensInString } from '@/utils/functionalUtils';
import { TRADE_CARD } from '@/utils/strings';
import { FC, useEffect, useMemo, useState } from 'react';
import { TradeFormDataInterface } from './interfaces/TradeInterfaces';
interface Props {
  tradeData: TradeFormDataInterface;
  handleSuccessfulTradeReceipt: () => void;
}
const TradeConfirmationReceipt: FC<Props> = ({
  tradeData,
  handleSuccessfulTradeReceipt: doneButtonClickHandler,
}) => {
  const { disableBackNavigation, enableBackNavigation } = useNavigation();
  const { sellAsset, buyAsset, amount } = tradeData;
  const [snapshotData] = useState(() => ({
    sellAsset: tradeData.sellAsset,
    buyAsset: tradeData.buyAsset,
    amountInTokenCurrency: parseFloat(tradeData.response?.amount || '0'),
    amountInUsd: parseFloat(tradeData.response?.amountInUsd || '0'),
    networkFee:
      parseFloat(tradeData.response?.gasCost || '0') +
      parseFloat(tradeData.response?.bridgeFee || '0'),
    networkFeeInUsd:
      parseFloat(tradeData.response?.gasCostInUsd || '0') +
      parseFloat(tradeData.response?.bridgeFeeInUsd || '0'),
    convenienceFee: parseFloat(tradeData.response?.sliverFee || '0'),
    convenienceFeeInUsd: parseFloat(tradeData.response?.sliverFeeInUsd || '0'),
  }));
  const replacements = useMemo(() => {
    const convenienceFeePercent =
      (snapshotData.convenienceFeeInUsd / snapshotData.amountInUsd) * 100;
    return {
      sell_asset_name: snapshotData.sellAsset?.name || '',
      sell_asset_symbol: snapshotData.sellAsset?.symbol || '',
      buy_asset_name: snapshotData.buyAsset?.name || '',
      buy_asset_symbol: snapshotData.buyAsset?.symbol || '',
      convenience_fee_percent: convenienceFeePercent.toFixed(2),
    };
  }, [sellAsset, buyAsset, amount]);
  useEffect(() => {
    disableBackNavigation();
    return () => enableBackNavigation();
  }, [disableBackNavigation, enableBackNavigation]);
  const rows = useMemo(() => {
    const buyAsset = snapshotData.buyAsset;
    const sellAsset = snapshotData.sellAsset;
    const sellAmount = snapshotData.amountInTokenCurrency;
    const sellAmountInUsd = snapshotData.amountInUsd;
    const networkFee = snapshotData.networkFee;
    const networkFeeInUsd = snapshotData.networkFeeInUsd;
    const convenienceFee = snapshotData.convenienceFee;
    const convenienceFeeInUsd = snapshotData.convenienceFeeInUsd;
    return [
      {
        cells: [
          { mainText: 'Bought' },
          {
            mainText: (
              <div className="flex items-center gap-sm">
                <TokenListItem
                  token={buyAsset as WalletTokenInterface}
                  align="right"
                  showTicker={true}
                  showNetwork
                  showNetworkPrefix
                />
                <Tooltip
                  trigger={<Icon name="question" intent="accent" />}
                  align="right"
                  isFullBleed
                >
                  <InfoAlert
                    title={replaceTokensInString(
                      TRADE_CARD.SCREENS.TRADE_CONFIRMATION_RECEIPT.MESSAGES
                        .TOOLTIPS.BUY_ASSET_TITLE,
                      replacements,
                    )}
                    description={replaceTokensInString(
                      TRADE_CARD.SCREENS.TRADE_CONFIRMATION_RECEIPT.MESSAGES
                        .TOOLTIPS.BUY_ASSET_DESCRIPTION,
                      replacements,
                    )}
                    intent="transparent"
                  />
                </Tooltip>
              </div>
            ),
            align: 'right' as `right`,
          },
        ],
      },
      {
        cells: [
          { mainText: 'Sold' },
          {
            mainText: (
              <div className="flex items-center gap-sm">
                <WalletTokenListItem
                  token={sellAsset as WalletTokenInterface}
                  align="right"
                  showBalance={false}
                  showWalletAddress={false}
                  showTicker={true}
                  showNetwork
                  showNetworkPrefix
                  walletBlockchain={
                    (sellAsset as WalletTokenInterface)?.walletBlockchain
                  }
                />
                <Tooltip
                  trigger={<Icon name="question" intent="accent" />}
                  align="right"
                  isFullBleed
                >
                  <InfoAlert
                    title={replaceTokensInString(
                      TRADE_CARD.SCREENS.TRADE_CONFIRMATION_RECEIPT.MESSAGES
                        .TOOLTIPS.SELL_ASSET_TITLE,
                      replacements,
                    )}
                    description={replaceTokensInString(
                      TRADE_CARD.SCREENS.TRADE_CONFIRMATION_RECEIPT.MESSAGES
                        .TOOLTIPS.SELL_ASSET_DESCRIPTION,
                      replacements,
                    )}
                    intent="transparent"
                  />
                </Tooltip>
              </div>
            ),
            align: 'right' as `right`,
          },
        ],
      },
      {
        cells: [
          { mainText: 'Amount' },
          {
            mainText: (
              <div className="flex items-center gap-sm">
                <AmountListItem
                  token={sellAsset as TokenInterface}
                  amount={sellAmount}
                  subAmount={sellAmountInUsd}
                />
                <Tooltip
                  trigger={<Icon name="question" intent="accent" />}
                  align="right"
                  isFullBleed
                >
                  <InfoAlert
                    title={replaceTokensInString(
                      TRADE_CARD.SCREENS.TRADE_CONFIRMATION_RECEIPT.MESSAGES
                        .TOOLTIPS.AMOUNT_TITLE,
                      replacements,
                    )}
                    description={replaceTokensInString(
                      TRADE_CARD.SCREENS.TRADE_CONFIRMATION_RECEIPT.MESSAGES
                        .TOOLTIPS.AMOUNT_DESCRIPTION,
                      replacements,
                    )}
                    intent="transparent"
                  />
                </Tooltip>
              </div>
            ),
            align: 'right' as 'right',
          },
        ],
      },
      {
        cells: [
          { mainText: 'Network Fee' },
          {
            mainText: (
              <div className="flex items-center gap-sm">
                <AmountListItem
                  token={sellAsset as TokenInterface}
                  amount={networkFee}
                  subAmount={networkFeeInUsd}
                />
                <Tooltip
                  trigger={<Icon name="question" intent="accent" />}
                  align="right"
                  isFullBleed
                >
                  <InfoAlert
                    title={replaceTokensInString(
                      TRADE_CARD.SCREENS.TRADE_CONFIRMATION_RECEIPT.MESSAGES
                        .TOOLTIPS.NETWORK_FEE_TITLE,
                      replacements,
                    )}
                    description={replaceTokensInString(
                      TRADE_CARD.SCREENS.TRADE_CONFIRMATION_RECEIPT.MESSAGES
                        .TOOLTIPS.NETWORK_FEE_DESCRIPTION,
                      replacements,
                    )}
                    intent="transparent"
                  />
                </Tooltip>
              </div>
            ),
            align: 'right' as 'right',
          },
        ],
      },
      {
        cells: [
          { mainText: 'Service Fee' },
          {
            mainText: (
              <div className="flex items-center gap-sm">
                {convenienceFee === 0 ? (
                  <Chip
                    text={`${TRADE_CARD.SCREENS.TRADE_CONFIRMATION_RECEIPT.MESSAGES.CHIP_LABEL_FOR_FREE}`}
                    intent="accent"
                    setChipToNoMargin={true}
                  />
                ) : (
                  <AmountListItem
                    token={sellAsset as TokenInterface}
                    amount={convenienceFee}
                    subAmount={convenienceFeeInUsd}
                  />
                )}
                <Tooltip
                  trigger={<Icon name="question" intent="accent" />}
                  align="right"
                  isFullBleed
                >
                  <InfoAlert
                    title={replaceTokensInString(
                      TRADE_CARD.SCREENS.TRADE_CONFIRMATION_RECEIPT.MESSAGES
                        .TOOLTIPS.CONVENIENCE_FEE_TITLE,
                      replacements,
                    )}
                    description={replaceTokensInString(
                      TRADE_CARD.SCREENS.TRADE_CONFIRMATION_RECEIPT.MESSAGES
                        .TOOLTIPS.CONVENIENCE_FEE_DESCRIPTION,
                      replacements,
                    )}
                    intent="transparent"
                  />
                </Tooltip>
              </div>
            ),
            align: 'right' as 'right',
          },
        ],
      },
    ];
  }, [sellAsset, buyAsset, amount, replacements]);
  return (
    <div className="flex flex-col flex-grow w-full h-full gap-sm">
      <Section body={<Table rows={rows} columnWidths={[50, 50]} />} />
      <SuccessAlert
        title={
          TRADE_CARD.SCREENS.TRADE_CONFIRMATION_RECEIPT.MESSAGES
            .TRANSACTION_PROCESSING_TITLE
        }
        description={
          TRADE_CARD.SCREENS.TRADE_CONFIRMATION_RECEIPT.MESSAGES
            .TRANSACTION_PROCESSING_DESCRIPTION
        }
      />
      <RatingFormAlert type={RatingFormType.GENERAL} />
      <ReferralCodeSection includeApplyReferralCodeSection={false} />
      <Button onClick={doneButtonClickHandler} fullWidth>
        {TRADE_CARD.SCREENS.TRADE_CONFIRMATION_RECEIPT.LABELS.DONE_BUTTON}
      </Button>
    </div>
  );
};
export default TradeConfirmationReceipt;
